import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/context/i18n";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MapPane } from "@/components/MapPane";
import { WatchButton } from "@/components/WatchButton";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, Check, BedDouble, XCircle } from "lucide-react";

const Stars = ({ n }) => <span className="text-[#009E9E]">{"★".repeat(n || 0)}</span>;

export default function HotelDetail() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useApp();
  const { t } = useI18n();
  const [hotel, setHotel] = useState(location.state?.hotel || null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  const checkIn = params.get("checkIn"), checkOut = params.get("checkOut");
  const adults = params.get("adults") || 2, children = params.get("children") || 0, rooms = params.get("rooms") || 1;
  const source = params.get("source") || "hbx";

  useEffect(() => {
    setLoading(true);
    api.get(`/hotels/${code}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&rooms=${rooms}&source=${source}`)
      .then((r) => setHotel(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [code]);

  const reserve = (room, rate) => {
    navigate("/checkout", { state: { item: {
      productType: "hotel", source,
      title: hotel.name,
      subtitle: `${room.name} · ${rate.boardName} · ${checkIn} → ${checkOut}`,
      image: hotel.images?.[0], priceEur: rate.net, rateKey: rate.rateKey,
      meta: { adults: +adults, children: +children, rooms: +rooms, destination: hotel.destinationName, roomName: room.name, board: rate.boardName, checkIn, checkOut },
    } } });
  };

  if (loading && !hotel) return (
    <div className="min-h-screen bg-[#F8FAFC]"><Header />
      <div className="pt-40 flex flex-col items-center text-[#475569]"><Loader2 className="w-8 h-8 animate-spin mb-3 text-[#009E9E]" />{t("loading_hotel")}</div></div>
  );
  if (!hotel) return <div className="min-h-screen bg-[#F8FAFC]"><Header /><div className="pt-32 text-center text-[#475569]">{t("hotel_unavailable")}</div></div>;

  const images = hotel.images?.length ? hotel.images : [];
  const watchPayload = {
    hotelCode: hotel.code, hotelName: hotel.name, image: hotel.images?.[0],
    destination: hotel.destinationName || "", checkIn, checkOut,
    adults: +adults, children: +children, rooms: +rooms,
    source, currentPriceEur: hotel.minRate ?? null,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Stars n={hotel.stars} />
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038]">{hotel.name}</h1>
          <WatchButton payload={watchPayload}
            className="ml-auto rounded-full border border-[#0B2038] text-[#0B2038] hover:bg-[#0B2038] hover:text-white h-10 px-4 text-sm" />
        </div>
        <p className="text-[#475569] flex items-center gap-1 mb-6"><MapPin className="w-4 h-4" />{hotel.address || `${hotel.zoneName || ""} ${hotel.destinationName || ""}`}</p>

        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mb-8">
            <div className="col-span-4 md:col-span-3 h-[360px] rounded-2xl overflow-hidden">
              <img src={images[activeImg]} alt={hotel.name} className="w-full h-full object-cover" />
            </div>
            <div className="col-span-4 md:col-span-1 flex md:flex-col gap-3 overflow-x-auto no-scrollbar">
              {images.slice(0, 5).map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 md:h-[68px] w-28 md:w-full rounded-xl overflow-hidden shrink-0 border-2 ${activeImg === i ? "border-[#009E9E]" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold text-[#0B2038] mb-2">{t("about")}</h2>
            <p className="text-[#475569] leading-relaxed mb-6">{hotel.description || ""}</p>
            {hotel.facilities?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-2 mb-8">
                {hotel.facilities.map((f, i) => <div key={i} className="flex items-center gap-2 text-sm text-[#0B2038]"><Check className="w-4 h-4 text-[#009E9E]" />{f}</div>)}
              </div>
            )}
            {hotel.latitude && hotel.longitude && (
              <div className="mb-8" data-testid="hotel-location-section">
                <h2 className="font-display text-2xl font-bold text-[#0B2038] mb-3">{t("location")}</h2>
                <div className="h-64 rounded-2xl overflow-hidden border border-[#E2E8F0]">
                  <MapPane hotels={[hotel]} formatPrice={formatPrice} />
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 sticky top-24">
              <p className="label-caps mb-3">{t("choose_room")}</p>
              <div className="space-y-4 max-h-[520px] overflow-y-auto">
                {hotel.rooms?.length ? hotel.rooms.map((room) => room.rates.map((rate, ri) => (
                  <div key={room.code + ri} data-testid={`room-rate-${room.code}-${ri}`} className="border border-[#E2E8F0] rounded-xl p-4">
                    <div className="flex items-start gap-2">
                      <BedDouble className="w-4 h-4 text-[#009E9E] mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#0B2038] text-sm leading-tight">{room.name}</p>
                        <p className="text-xs text-[#475569]">{rate.boardName}</p>
                        <p className={`text-xs mt-1 flex items-center gap-1 ${rate.rateType === "RECHECK" ? "text-[#475569]" : "text-green-600"}`}>
                          {rate.rateType === "RECHECK" ? <XCircle className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                          {rate.rateType === "RECHECK" ? t("on_request") : t("confirmed_instantly")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="font-display text-xl font-bold text-[#009E9E]">{formatPrice(rate.net)}</p>
                      <Button data-testid={`reserve-${room.code}-${ri}`} onClick={() => reserve(room, rate)} className="rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white h-9 px-5 text-sm font-semibold">{t("reserve")}</Button>
                    </div>
                  </div>
                ))) : <p className="text-sm text-[#475569]">{t("no_rooms")}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
